"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare, Send, User, CornerDownRight } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ErrorBoundary } from "react-error-boundary";

type Comment = {
  id: string;
  user_name: string;
  email?: string;
  content: string;
  created_at: string;
  parentId?: string | null;
  replies?: Comment[];
};

type CommentFormData = {
  name: string;
  email?: string;
  content: string;
  parentId?: string | null;
};

type CommentSectionProps = {
  comments: Comment[];
  onAddComment: (comment: CommentFormData) => Promise<void>;
  className?: string;
  isLoading?: boolean;
  offlineMode?: boolean;
};

// Error fallback component for comment section
function CommentErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="p-4 border border-red-300 bg-red-50 rounded-md text-red-800">
      <p className="font-medium">Something went wrong displaying comments</p>
      <p className="text-sm mt-2">{error.message}</p>
      <Button 
        variant="outline" 
        onClick={resetErrorBoundary}
        className="mt-4 text-sm"
      >
        Try again
      </Button>
    </div>
  );
}

export function CommentSection({ comments, onAddComment, className, isLoading, offlineMode }: CommentSectionProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyName, setReplyName] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    
    if (!name.trim()) {
      setErrorMessage("Please enter your name");
      return;
    }
    
    if (!content.trim()) {
      setErrorMessage("Please enter a comment");
      return;
    }

    try {
      setIsSubmitting(true);
      await onAddComment({
        name,
        email,
        content,
        parentId: null,
      });
      setContent("");
      setSuccessMessage("Comment submitted successfully!");
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error submitting comment:", error);
      // The parent component handles errors with client-side fallback
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    
    if (!replyName.trim()) {
      setErrorMessage("Please enter your name");
      return;
    }
    
    if (!replyContent.trim()) {
      setErrorMessage("Please enter a reply");
      return;
    }
    
    if (!replyTo) {
      setErrorMessage("Reply target is missing");
      return;
    }

    try {
      setIsSubmitting(true);
      await onAddComment({
        name: replyName,
        email: replyEmail,
        content: replyContent,
        parentId: replyTo,
      });
      setReplyContent("");
      setReplyName("");
      setReplyEmail("");
      setReplyTo(null);
      setSuccessMessage("Reply submitted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error adding reply:", error);
      // Let the parent component handle errors with its client-side fallback
      setReplyContent("");
      setReplyName("");
      setReplyEmail("");
      setReplyTo(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render a single comment and its replies
  const renderComment = (comment: Comment) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isReplyingToThis = replyTo === comment.id;
    
    return (
      <div key={comment.id} className="border-b pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 p-2 rounded-full">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium">{comment.user_name}</h4>
            <p className="text-sm text-muted-foreground mb-2">
              {formatDate(comment.created_at)}
            </p>
            <div className="mb-2 whitespace-pre-wrap">{comment.content}</div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setReplyTo(isReplyingToThis ? null : comment.id)}
              className="text-xs hover:bg-primary/10"
              aria-expanded={isReplyingToThis}
              aria-controls={isReplyingToThis ? `reply-form-${comment.id}` : undefined}
            >
              {isReplyingToThis ? "Cancel" : "Reply"}
            </Button>
          </div>
        </div>
        
        {isReplyingToThis && (
          <div 
            id={`reply-form-${comment.id}`}
            className="ml-10 mt-4 border-l-2 border-primary/20 pl-4"
            aria-label={`Reply to ${comment.user_name}'s comment`}
          >
            <form 
              onSubmit={handleSubmitReply} 
              className="space-y-4"
              action="/api/blog/comments" 
              method="post"
            >
              {/* Hidden fields for no-JS fallback */}
              <input type="hidden" name="slug" value={typeof window !== 'undefined' ? window.location.pathname.split('/').pop() || '' : ''} />
              <input type="hidden" name="parentId" value={comment.id} />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`reply-name-${comment.id}`} className="block text-sm font-medium mb-1">Name *</label>
                  <Input 
                    id={`reply-name-${comment.id}`} 
                    name="name"
                    value={replyName} 
                    onChange={(e) => setReplyName(e.target.value)} 
                    placeholder="Your name" 
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label htmlFor={`reply-email-${comment.id}`} className="block text-sm font-medium mb-1">Email (optional)</label>
                  <Input 
                    id={`reply-email-${comment.id}`} 
                    name="email"
                    type="email" 
                    value={replyEmail} 
                    onChange={(e) => setReplyEmail(e.target.value)} 
                    placeholder="Your email" 
                  />
                </div>
              </div>
              <div>
                <label htmlFor={`reply-content-${comment.id}`} className="block text-sm font-medium mb-1">Reply *</label>
                <Textarea 
                  id={`reply-content-${comment.id}`} 
                  name="content"
                  value={replyContent} 
                  onChange={(e) => setReplyContent(e.target.value)} 
                  placeholder="Write your reply..." 
                  rows={3} 
                  required
                  aria-required="true"
                />
              </div>
              <Button 
                type="submit" 
                size="sm"
                className="flex items-center gap-2" 
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                <CornerDownRight className="h-3 w-3" />
                {isSubmitting ? "Posting..." : "Post Reply"}
              </Button>
            </form>
          </div>
        )}
        
        {hasReplies && (
          <div 
            className="ml-10 mt-4 border-l-2 border-primary/20 pl-4 space-y-4"
            role="region"
            aria-label={`Replies to ${comment.user_name}'s comment`}
          >
            {comment.replies?.map(reply => (
              <div key={reply.id} className="pb-4 last:pb-0" role="comment">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium">{reply.user_name}</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      {formatDate(reply.created_at)}
                    </p>
                    <div className="whitespace-pre-wrap">{reply.content}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      <ErrorBoundary FallbackComponent={CommentErrorFallback}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Comments ({comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative" role="alert">
                <span className="block sm:inline">{errorMessage}</span>
              </div>
            )}
            
            {successMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 relative" role="alert">
                <span className="block sm:inline">{successMessage}</span>
              </div>
            )}
            
            {offlineMode && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 relative" role="alert">
                <span className="block sm:inline">You're in offline mode. Comments will be stored locally until connection is restored.</span>
              </div>
            )}
            
            <form 
              onSubmit={handleSubmitComment} 
              className="space-y-4 mb-8"
              action="/api/blog/comments" 
              method="post"
            >
              {/* Hidden slug field for no-JS fallback */}
              <input type="hidden" name="slug" value={typeof window !== 'undefined' ? window.location.pathname.split('/').pop() || '' : ''} />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">Name *</label>
                  <Input 
                    id="name" 
                    name="name"
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    placeholder="Your name" 
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">Email (optional)</label>
                  <Input 
                    id="email" 
                    name="email"
                    type="email" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    placeholder="Your email" 
                  />
                </div>
              </div>
              <div>
                <label htmlFor="comment" className="block text-sm font-medium mb-1">Comment *</label>
                <Textarea 
                  id="comment" 
                  name="content"
                  value={content} 
                  onChange={(e) => setContent(e.target.value)} 
                  placeholder="Write a comment..." 
                  rows={4} 
                  required
                  aria-required="true"
                />
              </div>
              <Button 
                type="submit" 
                className="flex items-center gap-2" 
                disabled={isSubmitting || isLoading}
                aria-busy={isSubmitting}
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? "Posting..." : "Post Comment"}
              </Button>
            </form>
            
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Recent Comments</h3>
              
              {comments.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Be the first to comment!
                </p>
              ) : (
                comments.map(comment => renderComment(comment))
              )}
            </div>
          </CardContent>
        </Card>
      </ErrorBoundary>
    </div>
  );
}
